import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { APPLICATIONS_QUERY } from 'features/properties/components/edit/queries';
import { USER_GROUP_QUERY } from '../../queries';
import useLanguageTracker from 'hooks/useLanguageTracker';
import { FakeText, Card } from '@totalsoft/rocket-ui';
import UserGroupGeneralDataComponent from './UserGroupGeneralDataComponent';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { userGroupNameVar } from '../../variables';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { SAVE_USER_GROUP } from '../../mutations';
import { USER_GROUP_SAVED } from '../../subscriptions';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { userGroupValidator } from '../../validators';
import { emptyArray } from 'utils/constants';

const UserGroupGeneralDataContainer = ({ userGroupId, onRedirect }) => {
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens({});
  const [validation, validate] = useDirtyFieldValidation(userGroupValidator);
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();
  const addToast = useToast();

  const { data: applicationsData } = useQuery(APPLICATIONS_QUERY);

  const { loading, refetch } = useQuery(USER_GROUP_QUERY, {
    variables: { userGroupId },
    skip: userGroupId === undefined || userGroupId === null,
    onCompleted: data => {
      resetLens(data.userGroup);
    }
  });

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useEffect(() => {
    const code = get(lens.code) ? get(lens.code) : '-';
    const name = get(lens.name) ? get(lens.name) : '-';
    const concatenatedNameVar = `${name} / ${code}`;
    userGroupNameVar(concatenatedNameVar);
  }, [lens]);

  const onLanguageChange = useCallback(() => {
    refetch();
  }, [refetch]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  useEffect(() => {
    validate(get(lens), dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  const [saveUserGroup] = useMutation(SAVE_USER_GROUP, {
    onError: () => {
      setSaving(false);
    }
  });

  const handleSave = useCallback(() => {
    const userGroup = get(lens);
    if (!validate(userGroup)) return;

    const userGroupInput = {
      id: null,
      ...userGroup,
      appCodes: userGroup.appCodes || emptyArray
    };

    setSaving(true);
    saveUserGroup({ variables: { input: userGroupInput } });
  }, [lens, validate, saveUserGroup]);

  useSubscription(USER_GROUP_SAVED, {
    onData: ({
      data: {
        data: { userGroupSaved }
      },
      client
    }) => {
      if (!userGroupId) onRedirect(userGroupSaved.userGroup.id);
      resetLens(userGroupSaved.userGroup);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'userGroupList' });
      setSaving(false);
      addToast(t('UserGroups.UserGroupSaved'), 'success');
    }
  });

  return (
    <>
      {loading ? (
        <Card>
          <FakeText lines={7} />
        </Card>
      ) : (
        <>
          <UserGroupGeneralDataComponent
            lens={lens}
            applications={applicationsData?.applicationsConfig || []}
            isDirty={isDirty}
            saving={saving}
            onSave={handleSave}
            validation={validation}
          />
        </>
      )}
    </>
  );
};

UserGroupGeneralDataContainer.propTypes = {
  userGroupId: PropTypes.number,
  onRedirect: PropTypes.func
};

export default UserGroupGeneralDataContainer;
